import * as React from 'react';
import PropTypes from 'prop-types';
import { Radio, RadioGroup, FormControlLabel, Button, CircularProgress, TextField, MenuItem } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import styles from './HB1416TrackingStudent.styles';
import HB1416SessionLog from '../HB1416SessionLog';

const SUBJECT_LABELS = { math: 'Math', reading: 'Reading' };

const EXCLUSION_REASONS = [
  { value: 'tiaTeacher', label: 'Assigned to TIA-designated teacher' },
  { value: 'manual', label: 'Manual/other' },
];

const NARRATIVE_MAX_CHARS = 4000;

// Backend completedHours already folds session minutes + ixlMinutes/60 (single source
// of truth), so read it directly. ixlMinutes is shown as an informational breakdown only.
const completedForSubject = (tracker, subject) => (tracker.completedHours && tracker.completedHours[subject]) || 0;

const percentForSubject = (tracker, subject) => {
  const required = (tracker.requiredHours && tracker.requiredHours[subject]) || 0;
  if (!required) return 0;
  const completed = completedForSubject(tracker, subject);
  return Math.min(100, Math.round((completed / required) * 100));
};

class HB1416TrackingStudent extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.object,
    firebaseId: PropTypes.string.isRequired,
    snapshotYear: PropTypes.string,
    student: PropTypes.object,
    tracker: PropTypes.object,
    school: PropTypes.string,
    aepGenerating: PropTypes.object,
    onLogSession: PropTypes.func.isRequired,
    onUpdateFields: PropTypes.func.isRequired,
    onGenerateAEP: PropTypes.func.isRequired,
  };

  static defaultProps = {
    student: {},
    tracker: {},
    aepGenerating: {},
  };

  // narrative === null means "not edited yet": the textarea shows the saved value.
  state = { language: 'en', exclusionReason: 'tiaTeacher', exclusionNote: '', narrative: null };

  handleLanguageChange = (event) => {
    this.setState({ language: event.target.value });
  };

  handleGenerate = () => {
    const { snapshotYear, firebaseId, onGenerateAEP } = this.props;
    const { language } = this.state;
    onGenerateAEP(snapshotYear, firebaseId, language);
  };

  handleExclusionReasonChange = (event) => {
    this.setState({ exclusionReason: event.target.value });
  };

  handleExclusionNoteChange = (event) => {
    this.setState({ exclusionNote: event.target.value });
  };

  handleExclude = () => {
    const { snapshotYear, firebaseId, onUpdateFields } = this.props;
    const { exclusionReason, exclusionNote } = this.state;
    const exclusion = { reason: exclusionReason };
    if (exclusionNote) exclusion.note = exclusionNote;
    onUpdateFields(snapshotYear, firebaseId, null, { exclusion });
  };

  handleUndoExclusion = () => {
    const { snapshotYear, firebaseId, onUpdateFields } = this.props;
    // Clearing only removes the exclusion; the next recompute restores the tags.
    onUpdateFields(snapshotYear, firebaseId, null, { exclusion: null });
  };

  handleNarrativeChange = (event) => {
    this.setState({ narrative: event.target.value.slice(0, NARRATIVE_MAX_CHARS) });
  };

  handleSaveNarrative = () => {
    const { snapshotYear, firebaseId, onUpdateFields } = this.props;
    const { narrative } = this.state;
    if (narrative === null) return;
    onUpdateFields(snapshotYear, firebaseId, null, { aepNarrative: narrative });
  };

  narrativeValue() {
    const { tracker } = this.props;
    const { narrative } = this.state;
    return narrative === null ? tracker.aepNarrative || '' : narrative;
  }

  renderHoursBlock(subject) {
    const { classes, tracker } = this.props;
    const required = (tracker.requiredHours && tracker.requiredHours[subject]) || 0;
    if (!required) return null;
    const completed = completedForSubject(tracker, subject);
    const percent = percentForSubject(tracker, subject);
    return (
      <div className={classes.hoursBlock} key={subject}>
        <div className={classes.hoursLabel}>{SUBJECT_LABELS[subject] || subject}</div>
        <div className={classes.hoursValue}>
          {completed.toFixed(1)} of {required} hours ({percent}%)
        </div>
        <div className={classes.progressBarOuter}>
          <div className={classes.progressBarInner} style={{ width: `${percent}%` }} />
        </div>
      </div>
    );
  }

  renderExclusionSection() {
    const { classes, tracker } = this.props;
    const { exclusionReason, exclusionNote } = this.state;
    const exclusion = tracker.exclusion;
    if (exclusion) {
      const reasonOption = EXCLUSION_REASONS.find((option) => option.value === exclusion.reason);
      return (
        <div className={classes.exclusionSection}>
          <div className={classes.exclusionTitle}>Excluded from HB 1416</div>
          <div className={classes.exclusionMeta}>
            {(reasonOption && reasonOption.label) || exclusion.reason}
            {exclusion.teacher ? ` · ${exclusion.teacher}` : ''}
            {exclusion.designation ? ` (${exclusion.designation})` : ''}
            {exclusion.note ? ` · ${exclusion.note}` : ''}
          </div>
          <Button variant="outlined" size="small" onClick={this.handleUndoExclusion}>
            Undo Exclusion
          </Button>
        </div>
      );
    }
    return (
      <div className={classes.exclusionSection}>
        <div className={classes.exclusionTitle}>Exclude from HB 1416</div>
        <div className={classes.exclusionControls}>
          <TextField
            className={classes.exclusionReason}
            select
            label="Reason"
            value={exclusionReason}
            onChange={this.handleExclusionReasonChange}
          >
            {EXCLUSION_REASONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            className={classes.exclusionNote}
            label="Note (optional)"
            value={exclusionNote}
            onChange={this.handleExclusionNoteChange}
          />
          <Button variant="outlined" onClick={this.handleExclude}>
            Exclude from HB 1416
          </Button>
        </div>
      </div>
    );
  }

  renderAepStatus() {
    const { classes, firebaseId, aepGenerating, tracker } = this.props;
    const live = aepGenerating[firebaseId];
    if (live && live.status === 'generating') {
      return (
        <span className={classes.aepStatus}>
          <CircularProgress size={16} /> Generating ...
        </span>
      );
    }
    if (live && live.status === 'error') {
      return <span className={classes.aepError}>{live.error || 'Generation failed'}</span>;
    }
    const pdfUrl = (live && live.pdfUrl) || (tracker.aep && tracker.aep.pdfUrl);
    if (pdfUrl) {
      return (
        <a className={classes.downloadLink} href={pdfUrl} target="_blank" rel="noopener noreferrer">
          Download AEP PDF
        </a>
      );
    }
    if (tracker.aep && tracker.aep.status === 'generated') {
      return <span className={classes.aepStatus}>AEP generated</span>;
    }
    return <span className={classes.aepStatus}>Not generated yet</span>;
  }

  render() {
    const { classes, firebaseId, snapshotYear, student, tracker, school, onLogSession, aepGenerating } = this.props;
    const { language } = this.state;
    const fullName = `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student';
    const aepRequired = Boolean(tracker.aep && tracker.aep.required);
    const excluded = Boolean(tracker.exclusion);
    const live = aepGenerating[firebaseId];
    const isGenerating = Boolean(live && live.status === 'generating');
    const narrativeValue = this.narrativeValue();
    const narrativeDirty = this.state.narrative !== null && this.state.narrative !== (tracker.aepNarrative || '');

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <div>
            <div className={classes.studentName}>{fullName}</div>
            <div className={classes.studentMeta}>
              {school ? `${school} · ` : ''}
              {student.gradeLevel != null ? `Grade ${student.gradeLevel}` : ''}
            </div>
          </div>
        </div>

        <div className={classes.hoursGrid}>
          {this.renderHoursBlock('math')}
          {this.renderHoursBlock('reading')}
        </div>

        <HB1416SessionLog
          firebaseId={firebaseId}
          snapshotYear={snapshotYear}
          sessions={tracker.sessions || []}
          onLogSession={onLogSession}
        />

        {this.renderExclusionSection()}

        <div className={classes.aepSection}>
          <div className={classes.aepSectionTitle}>Accelerated Education Plan</div>
          <div className={classes.narrativeBlock}>
            <TextField
              className={classes.narrativeField}
              label="Plan narrative (leave blank for auto-generated)"
              value={narrativeValue}
              onChange={this.handleNarrativeChange}
              multiline
              rows={4}
              fullWidth
            />
            <Button variant="outlined" size="small" disabled={!narrativeDirty} onClick={this.handleSaveNarrative}>
              Save Narrative
            </Button>
          </div>
          <div className={classes.aepControls}>
            <RadioGroup row name={`aep-lang-${firebaseId}`} value={language} onChange={this.handleLanguageChange}>
              <FormControlLabel value="en" control={<Radio color="primary" />} label="English" />
              <FormControlLabel value="es" control={<Radio color="primary" />} label="Spanish" />
            </RadioGroup>
            <Button
              variant="contained"
              color="primary"
              disabled={!aepRequired || excluded || isGenerating}
              onClick={this.handleGenerate}
            >
              Generate AEP
            </Button>
            {this.renderAepStatus()}
          </div>
          {!aepRequired && <div className={classes.aepStatus}>An AEP is not required for this student.</div>}
          {excluded && <div className={classes.aepStatus}>This student is excluded from HB 1416.</div>}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(HB1416TrackingStudent);
